import type { ScenarioSpec } from "./types";
import { officeUnderAttackScenario as OUA } from "./office-under-attack.scenario";

/**
 * Reception-terminal briefing on sharing files.
 * The game is a separate scenario (send-it-safely) opened with skipQuiz.
 * Not on the public library until it is published.
 */
const VIDEO = "/cdn/sim/awareness-videos/sharing-files/sharing-files.mp4";
const FILM_STILL = "/cdn/sim/awareness-videos/sharing-files/wide/01-service.jpg";
const GAME_STILL = "/cdn/sim/awareness-videos/sharing-files/wide/06-named.jpg";

export const sharingFilesScenario: ScenarioSpec = {
  version: 1,
  id: "sharing-files",
  title: "Sharing files and collaborating safely",
  summary: {
    goal: "Learn to share the right file with the right people, give only the access a task needs, and remove access when the work is done.",
    youDo: "Watch a film, then work through the team's requests on the computer: check each file, choose who gets it and what they can do, and answer six questions.",
    duration: "Core 4-7 min, Send It Safely adds 8-12 min",
    tags: ["file_sharing", "data_protection"],
    audience: ["all_staff"],
    needs: ["sound"],
  },
  topic: "Share the right file with the right people, and only the access the task needs",
  language: "en",
  passThreshold: 60,
  allowFreeExplore: false,
  voiceAttackers: OUA.voiceAttackers,
  scene: OUA.scene,

  devices: [
    {
      id: "share_terminal",
      kind: "breachbrief",
      config: {
        noticeTag: "SHARING",
        noticeKicker: "SECURITY AWARENESS",
        title: "Sharing files and collaborating safely",
        offLabel: "Switch on the welcome terminal",
        voiceId: "onyx",
        progressKey: "sharing-files",
        idleImage: FILM_STILL,
        videoFirst: true,
        lines: [
          "Help your team share the right information with the right people. Open requests, check the files, and choose who can access them and what they can do.",
          "Some files can be shared publicly. Others need limited access. The quiz stays on this terminal until you are ready.",
        ],
        hubIntro: "Watch the film, then practise sharing decisions on the computer.",
        hubVideo: {
          title: "Sharing files",
          blurb: "Who can open a file, what they can do, and when to take access away.",
          src: VIDEO,
          poster: FILM_STILL,
        },
        exercisesKicker: "1 interactive exercise",
        exercisesLabel: "Practise",
        exercises: [
          {
            id: "send-it-safely",
            label: "Send It Safely",
            blurb: "Help a small team organise a workplace workshop. Check each file and choose who can access it.",
            key: "game",
            completionBeatId: "b_game",
            icon: "chapters",
            imageUrl: GAME_STILL,
            actionLabel: "Play",
            doneLabel: "Done",
            href: "/?scenario=send-it-safely&beat=b_to_monitor&return=sharing-files&skipQuiz=1",
          },
        ],
        cards: [],
        finishEarlyHint: "You can take the quiz now, or watch the film and play Send It Safely first.",
      },
    },
  ],

  beats: [
    {
      id: "b_intro",
      type: "intro",
      title: "Sharing files and collaborating safely",
      kicker: "Reception",
      guide: { title: "Start", body: "You start at an unattended reception. The welcome terminal shows a film on sharing files, then sends you to a computer where a small team is organising a workplace workshop and file requests start to come in." },
    },
    { id: "b_cam_terminal", type: "camera", waypointId: "wp_terminal_close", walkSfx: true },
    {
      id: "b_hub",
      type: "device",
      deviceId: "share_terminal",
      guide: {
        title: "Reception terminal",
        body: "Switch on the welcome terminal. The film is on the left. Send It Safely is beside it.",
      },
      completeOn: { action: "continue" },
    },
    {
      id: "b_quiz",
      type: "quiz",
      title: "Check what you would do",
      quiz: [
        {
          id: "q_morgan",
          title: "Question 1",
          prompt: "Two people in the address book have the same name. The request is for the colleague in your organisation. What do you check before you share?",
          options: [
            { id: "a", label: "The first name in the list is enough.", icon: "x" },
            { id: "b", label: "The full email address and organisation in your company directory.", correct: true, icon: "check" },
            { id: "c", label: "Share with both, so one of them is right.", icon: "alert" },
          ],
          rationale: "The same name can belong to two people. The address and organisation tell you which one is your colleague.",
        },
        {
          id: "q_copy",
          title: "Question 2",
          prompt: "An outside partner needs to suggest changes to a document. Which version do you share?",
          options: [
            { id: "a", label: "The internal working copy, because it has the latest comments.", icon: "x" },
            { id: "b", label: "The version approved for external review, with comment access only.", correct: true, icon: "check" },
            { id: "c", label: "A public link, so the invitation cannot fail.", icon: "alert" },
          ],
          rationale: "Internal comments and drafts stay internal. Share the approved version and give only the access the task needs.",
        },
        {
          id: "q_folder",
          title: "Question 3",
          prompt: "You invite the project team to a file, but someone who left the project can still open it. What is the likely reason?",
          options: [
            { id: "a", label: "A new invitation replaces every older way in.", icon: "x" },
            { id: "b", label: "The file still inherits access from a broader shared folder.", correct: true, icon: "check" },
            { id: "c", label: "Old access expires on its own at the end of the day.", icon: "alert" },
          ],
          rationale: "Files usually inherit the access of the folder they sit in. Move the file somewhere restricted, or fix the folder's access.",
        },
        {
          id: "q_public",
          title: "Question 4",
          prompt: "A flyer is approved for public distribution. What happens if you share it with a link that requires sign-in?",
          options: [
            { id: "a", label: "Nothing. Sign-in is always the safer choice.", icon: "x" },
            { id: "b", label: "The people it is meant for cannot open it without an account.", correct: true, icon: "check" },
            { id: "c", label: "The file is published in search engines.", icon: "alert" },
          ],
          rationale: "Pick the access that fits the file. Approved public material should open for anyone, with view access only.",
        },
        {
          id: "q_app",
          title: "Question 5",
          prompt: "An app you did not expect asks to read all your files and your mailbox and to send mail for you. What do you do?",
          options: [
            { id: "a", label: "Approve it. The page looks like a real provider.", icon: "x" },
            { id: "b", label: "Cancel it and report it. Use only approved apps, with the least access the task needs.", correct: true, icon: "check" },
            { id: "c", label: "Approve it, then change your password. That removes the app.", icon: "alert" },
          ],
          rationale: "Approving an app gives it access that a password change does not take away. Check what it asks for before you allow anything.",
        },
        {
          id: "q_close",
          title: "Question 6",
          prompt: "An outside helper has finished their part of a project. What should happen to their access?",
          options: [
            { id: "a", label: "Delete every file and every link, to be safe.", icon: "x" },
            { id: "b", label: "Remove the helper's remaining access. Keep what the team and the public still need.", correct: true, icon: "check" },
            { id: "c", label: "Leave their guest account in place in case they come back.", icon: "alert" },
          ],
          rationale: "Close the access the work no longer needs, and only that. A copy they already downloaded stays on their side.",
        },
      ],
    },
  ],

  edges: [
    { from: "b_intro", to: "b_cam_terminal" },
    { from: "b_cam_terminal", to: "b_hub" },
    { from: "b_hub", to: "b_quiz", on: "finish" },
  ],

  initialBeatId: "b_intro",
};
